import { useEffect, useRef } from 'react';
import { Box, Chip, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { stageLabelFor } from '@/components/Property/propertyCardConstants';
import { displayStepNumber } from '@/utils/propertyView/stageResolver';
import type { PropertyStage, PropertyView } from '@/types/PropertyView';

interface DealRoomPillsProps {
  property: PropertyView;
  currentStage: number;
}

export const DealRoomPills = ({
  property,
  currentStage,
}: DealRoomPillsProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [currentStage]);

  return (
    <Box
      ref={scrollerRef}
      sx={{
        display: 'flex',
        overflowX: 'auto',
        gap: 1,
        py: 1.5,
        px: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {property.visibleStages.map((stage: PropertyStage) => {
        const active = stage === (currentStage as PropertyStage);
        const completed = stage < property.stage;
        const locked = stage > property.stage;
        return (
          <Box
            key={stage}
            ref={active ? activeRef : undefined}
            sx={{ flex: '0 0 auto' }}
          >
            <Chip
              size="small"
              label={`${displayStepNumber(property, stage)}. ${stageLabelFor(
                property,
                stage
              )}`}
              color={active ? 'primary' : completed ? 'success' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              disabled={locked}
              onClick={() => {
                if (locked) return;
                navigate(`/properties/${property.id}/stage/${stage}`);
              }}
              sx={{ fontWeight: 600 }}
            />
          </Box>
        );
      })}
    </Box>
  );
};
